import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import type { Funcionario } from '@/context/FuncionariosContext';
import type { AvaliacaoSiadap } from '@/context/SiadapContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';

interface RegistoHistoricoItem {
  id: string;
  ano: number;
  periodoAvaliacao?: string;
  avaliacao?: AvaliacaoSiadap;
  pontosChefe: number;
  pontosCCA: number;
  classificacaoServico?: string;
  fonte: 'siadap' | 'historico';
}

interface FuncionarioHistoricoModalProps {
  funcionario: Funcionario;
  onClose: () => void;
}

const avaliacaoConfig: Record<string, { bg: string; text: string; dot: string; bar: string }> = {
  Excelente:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', bar: 'bg-emerald-500' },
  'Muito Bom': { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   bar: 'bg-amber-400'   },
  Bom:         { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400',    bar: 'bg-zinc-400'    },
  Regular:     { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-400',  bar: 'bg-orange-400'  },
  Inadequado:  { bg: 'bg-rose-50',    text: 'text-rose-700',    dot: 'bg-rose-500',    bar: 'bg-rose-500'    },
  Relevante:   { bg: 'bg-teal-50',    text: 'text-teal-700',    dot: 'bg-teal-500',    bar: 'bg-teal-500'    },
  Adequado:    { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400',    bar: 'bg-zinc-400'    },
};

const avaliacaoPontos: Record<string, number> = {
  Excelente: 3, 'Muito Bom': 2, Bom: 1.5, Regular: 1, Inadequado: 0,
  Relevante: 2, Adequado: 1.5,
};

const avatarColors = [
  { bg: 'bg-rose-100',   text: 'text-rose-700'   },
  { bg: 'bg-violet-100', text: 'text-violet-700' },
  { bg: 'bg-amber-100',  text: 'text-amber-700'  },
  { bg: 'bg-teal-100',   text: 'text-teal-700'   },
  { bg: 'bg-sky-100',    text: 'text-sky-700'    },
  { bg: 'bg-orange-100', text: 'text-orange-700' },
];

type TabId = 'avaliacoes' | 'progressoes';

export default function FuncionarioHistoricoModal({ funcionario, onClose }: FuncionarioHistoricoModalProps) {
  const [registos, setRegistos] = useState<RegistoHistoricoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('avaliacoes');
  const [anoFilter, setAnoFilter] = useState('');

  const colorIdx = parseInt(funcionario.id.replace(/\D/g, ''), 10) % avatarColors.length;
  const palette = avatarColors[isNaN(colorIdx) ? 0 : colorIdx];
  const initials = funcionario.nome
    .split(' ')
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0])
    .join('');

  useModalOverflow();
  const { animating, handleClose } = useModalAnimation(onClose);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); };
  }, [handleClose]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [{ data: siadapData }, { data: historicoData }] = await Promise.all([
          supabase.from('registos_siadap').select('*').eq('funcionario_id', funcionario.id).order('created_at'),
          supabase.from('historico_progressoes').select('*').eq('funcionario_id', funcionario.id).order('ano', { ascending: false }),
        ]);

        const siadapItems: RegistoHistoricoItem[] = (siadapData ?? []).map((row) => {
          const periodo = (row.periodo_avaliacao as string) ?? '';
          const anoMatch = periodo.match(/(\d{4})/);
          const ano = anoMatch ? parseInt(anoMatch[1], 10) : new Date().getFullYear();
          return {
            id: `siadap-${row.id}`,
            ano,
            periodoAvaliacao: row.periodo_avaliacao as string,
            avaliacao: row.avaliacao as AvaliacaoSiadap,
            pontosChefe: Number(row.pontos_chefe),
            pontosCCA: Number(row.pontos_cca),
            fonte: 'siadap',
          };
        });

        const historicoItems: RegistoHistoricoItem[] = (historicoData ?? []).map((row) => ({
          id: row.id as string,
          ano: row.ano as number,
          pontosChefe: Number(row.pontuacao_chefe),
          pontosCCA: Number(row.pontuacao_cca),
          classificacaoServico: row.classificacao_servico as string,
          fonte: 'historico',
        }));

        setRegistos([...siadapItems, ...historicoItems].sort((a, b) => b.ano - a.ano));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [funcionario.id]);

  const anos = useMemo(() => Array.from(new Set(registos.map((r) => r.ano))).sort((a, b) => b - a), [registos]);
  const filtered = useMemo(() => anoFilter ? registos.filter((r) => r.ano === parseInt(anoFilter, 10)) : registos, [registos, anoFilter]);

  const totalPontos = registos.filter((r) => r.fonte === 'siadap' && r.avaliacao)
    .reduce((s, r) => s + (avaliacaoPontos[r.avaliacao!] ?? 0), 0);
  const mediaChefe = registos.length > 0 ? (registos.reduce((s, r) => s + r.pontosChefe, 0) / registos.length).toFixed(2) : '—';
  const mediaCCA   = registos.length > 0 ? (registos.reduce((s, r) => s + r.pontosCCA,   0) / registos.length).toFixed(2) : '—';

  const tabs: { id: TabId; label: string; icon: string; count: number }[] = [
    { id: 'avaliacoes',  label: 'Avaliações',  icon: 'ri-bar-chart-line',      count: registos.length },
    { id: 'progressoes', label: 'Progressões', icon: 'ri-arrow-up-circle-line', count: 0 },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden border border-zinc-100 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-100 flex items-center gap-4 flex-shrink-0 bg-zinc-50/60">
          <div className={`w-11 h-11 flex items-center justify-center rounded-full text-[14px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-0.5">Ficha do Funcionário</p>
            <h2 className="font-bold text-zinc-900 text-[16px] leading-tight truncate">{funcionario.nome}</h2>
            <p className="text-[12px] text-zinc-400 mt-0.5">{funcionario.carreira} · <span className="font-mono">#{funcionario.id}</span></p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Stats bar */}
        <div className="px-6 py-3 bg-zinc-50/40 border-b border-zinc-100 flex items-center gap-5 flex-wrap flex-shrink-0">
          {[
            { icon: 'ri-history-line',     label: 'Registos',    value: registos.length,                  color: 'text-zinc-500'   },
            { icon: 'ri-user-star-line',   label: 'Média Chefe', value: mediaChefe,                       color: 'text-zinc-500'   },
            { icon: 'ri-shield-check-line',label: 'Média CCA',   value: mediaCCA,                         color: 'text-emerald-600'},
            { icon: 'ri-award-line',       label: 'Pts SIADAP',  value: `${totalPontos.toFixed(1)}`,      color: 'text-amber-600'  },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-2">
              <div className={`w-4 h-4 flex items-center justify-center ${s.color}`}>
                <i className={`${s.icon} text-xs`} />
              </div>
              <span className="text-[12px] text-zinc-500">{s.label}: <strong className={s.color}>{s.value}</strong></span>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-zinc-100 flex items-center gap-0 bg-white flex-shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap flex items-center gap-2 px-4 py-3.5 text-[13px] font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === tab.id
                  ? 'border-zinc-900 text-zinc-900'
                  : 'border-transparent text-zinc-400 hover:text-zinc-600'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${tab.icon} text-sm`} />
              </div>
              {tab.label}
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${
                activeTab === tab.id ? 'bg-zinc-100 text-zinc-700' : 'bg-zinc-100 text-zinc-400'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Tab: Avaliações */}
        {activeTab === 'avaliacoes' && (
          <>
            {/* Filter */}
            <div className="px-6 py-2.5 border-b border-zinc-100 flex items-center gap-3 flex-shrink-0">
              <select
                value={anoFilter}
                onChange={(e) => setAnoFilter(e.target.value)}
                className="text-[12px] border border-zinc-200 rounded-lg px-3 py-1.5 bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 cursor-pointer"
              >
                <option value="">Todos os anos ({registos.length})</option>
                {anos.map((a) => (
                  <option key={a} value={a}>{a} ({registos.filter((r) => r.ano === a).length})</option>
                ))}
              </select>
              {anoFilter && (
                <button onClick={() => setAnoFilter('')} className="whitespace-nowrap flex items-center gap-1 text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer">
                  <i className="ri-close-circle-line text-sm" />Limpar
                </button>
              )}
              <span className="ml-auto text-[11px] text-zinc-400">
                <span className="font-semibold text-zinc-600">{filtered.length}</span> de {registos.length}
              </span>
            </div>

            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <div className="w-8 h-8 flex items-center justify-center"><i className="ri-loader-4-line text-3xl animate-spin text-zinc-300" /></div>
                  <p className="text-[13px] text-zinc-400">A carregar...</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="sticky top-0 bg-white z-10 border-b border-zinc-100">
                    <tr>
                      {['Ano / Período', 'Avaliação', 'Chefia', 'CCA', 'Fonte'].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-widest">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r, i) => {
                      const isSiadap = r.fonte === 'siadap';
                      const cfg = r.avaliacao ? avaliacaoConfig[r.avaliacao] : null;
                      const pts = r.avaliacao ? avaliacaoPontos[r.avaliacao] : null;
                      const isFirst = i === 0 && !anoFilter;
                      return (
                        <tr key={r.id} className={`border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors ${isFirst ? 'bg-emerald-50/20' : ''}`}>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              {isFirst && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">Último</span>}
                              <span className="text-[13px] font-semibold text-zinc-700">{r.ano}</span>
                              {isSiadap && r.periodoAvaliacao && (
                                <span className="text-[11px] text-zinc-400">({r.periodoAvaliacao})</span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            {isSiadap && cfg && r.avaliacao ? (
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.bg} ${cfg.text}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                                  {r.avaliacao}
                                </span>
                                {pts !== null && (
                                  <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.text}`}>
                                    {pts % 1 === 0 ? pts.toFixed(0) : pts.toFixed(1)} pts
                                  </span>
                                )}
                              </div>
                            ) : r.classificacaoServico ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-zinc-100 text-zinc-600">
                                {r.classificacaoServico}
                              </span>
                            ) : <span className="text-[12px] text-zinc-300">—</span>}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-14 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                <div className="h-full bg-zinc-400 rounded-full" style={{ width: `${(r.pontosChefe / 5) * 100}%` }} />
                              </div>
                              <span className="text-[12px] font-medium text-zinc-700">{r.pontosChefe.toFixed(1)}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-14 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(r.pontosCCA / 5) * 100}%` }} />
                              </div>
                              <span className="text-[12px] font-medium text-zinc-700">{r.pontosCCA.toFixed(1)}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            {isSiadap ? (
                              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full font-medium">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />SIADAP
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full font-medium">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />Manual
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-5 py-16 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-10 h-10 flex items-center justify-center bg-zinc-50 rounded-full">
                              <i className="ri-history-line text-xl text-zinc-300" />
                            </div>
                            <p className="text-[13px] text-zinc-400">Sem avaliações registadas.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Tab: Progressões — placeholder */}
        {activeTab === 'progressoes' && (
          <div className="flex-1 flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-14 h-14 flex items-center justify-center bg-zinc-50 rounded-full border border-zinc-100">
              <i className="ri-arrow-up-circle-line text-3xl text-zinc-300" />
            </div>
            <p className="text-[13px] font-medium text-zinc-500">Sem progressões registadas</p>
            <p className="text-[12px] text-zinc-400 text-center max-w-xs">As progressões deste funcionário aparecem na página principal.</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-between flex-shrink-0 bg-zinc-50/40">
          <p className="text-[11px] text-zinc-400">
            Total acumulado (SIADAP): <span className="font-semibold text-zinc-600">{totalPontos.toFixed(1)} pts</span>
          </p>
          <button
            onClick={handleClose}
            className="whitespace-nowrap px-4 py-2 text-[13px] font-medium text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
